import React, { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, PenLine, Sparkles, Upload, X } from 'lucide-react';
import { SidePanel } from '../SidePanel';
import { errorText } from '../ErrorBanner';
import { intakeApi } from '../../services/intakeApi';
import { IntakeForm, IntakeReadResult, emptyIntake } from '../../types/intake';
import { IntakeFields } from './IntakeFields';

const ACCEPT = '.pdf,.hwp,.hwpx,.docx,.txt';
const MAX_FILES = 10;
const fmtSize = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`);

/**
 * 「프로젝트 추가」 (2026-10-22) — 넣기 → AI 가 접수서를 채움(창에서 기다림) → 확인 → 만들기.
 * 파일 · 글 · 직접 입력 셋 중 아무거나. 만들면 검토 단계 프로젝트 + 드라이브 01 폴더에 원본 · 접수서 Word.
 */
export const IntakePanel: React.FC<{
  userName: string;
  onClose: () => void;
  onCreated: (id: number, warnings: string[]) => void;
}> = ({ userName, onClose, onCreated }) => {
  const [step, setStep] = useState<'input' | 'reading' | 'confirm'>('input');
  const [files, setFiles] = useState<File[]>([]);
  const [text, setText] = useState('');
  const [form, setForm] = useState<IntakeForm>(() => emptyIntake(userName));
  const [read, setRead] = useState<IntakeReadResult['read']>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState(false);
  const [secs, setSecs] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  const add = (list: FileList | null) => {
    if (!list) return;
    const next = [...files, ...Array.from(list)].slice(0, MAX_FILES);
    if (files.length + list.length > MAX_FILES) setError(`파일은 ${MAX_FILES}개까지입니다`);
    setFiles(next);
  };

  const runAi = async () => {
    setError(null); setStep('reading'); setSecs(0);
    timer.current = setInterval(() => setSecs((s) => s + 1), 1000);
    try {
      const r = await intakeApi.read(files, text);
      setForm({ ...r.form, receivedBy: r.form.receivedBy || userName });
      setRead(r.read);
      setStep('confirm');
    } catch (e) {
      setError(errorText(e, 'AI 가 읽지 못했습니다'));
      setStep('input');
    } finally {
      if (timer.current) clearInterval(timer.current);
    }
  };

  const manual = () => {
    // 붙여 넣은 글은 메모로 옮겨 둔다 — 버리지 않는다
    setForm({ ...emptyIntake(userName), memo: text.trim() || null });
    setRead([]); setError(null); setStep('confirm');
  };

  const create = async () => {
    setSaving(true); setError(null);
    try {
      const r = await intakeApi.create(form, files);
      onCreated(r.id, r.warnings);
    } catch (e) {
      setError(errorText(e, '프로젝트를 만들지 못했습니다'));
      setSaving(false);
    }
  };

  const ready = !!form.bizName?.trim() && !!form.clientOrg?.trim();
  const missing = [form.clientOrg, form.bizName, form.budget, form.dueAt, form.contactName, form.scope].filter((v) => !v?.trim()).length;

  return (
    <SidePanel
      onClose={onClose}
      header={
        <div>
          <h2 className="text-base font-semibold text-slate-900">프로젝트 추가</h2>
          <p className="text-xs text-slate-500">
            {step === 'input' && '공고문 · 문의 메일 · 메모를 넣으면 AI 가 사업 접수서를 채웁니다. 직접 입력도 됩니다'}
            {step === 'reading' && 'AI 가 읽고 있습니다'}
            {step === 'confirm' && '칸을 확인하고 고친 뒤 만듭니다. 노란 칸은 원문에서 못 찾은 것입니다'}
          </p>
        </div>
      }
      footer={
        <div className="flex items-center gap-2">
          {step === 'confirm' && (
            <button onClick={() => setStep('input')} disabled={saving} className="px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40">← 다시 넣기</button>
          )}
          <span className="flex-1" />
          {step === 'input' && (
            <>
              <button onClick={manual} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">
                <PenLine size={14} /> 직접 입력
              </button>
              <button onClick={runAi} disabled={!files.length && !text.trim()}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
                <Sparkles size={14} /> AI 로 채우기
              </button>
            </>
          )}
          {step === 'confirm' && (
            <button onClick={create} disabled={saving || !ready} title={ready ? '' : '발주처와 사업명은 꼭 넣어야 합니다'}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
              {saving && <Loader2 size={14} className="animate-spin" />} 프로젝트 만들기
            </button>
          )}
        </div>
      }
    >
      {error && <p role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      {step === 'input' && (
        <div className="space-y-4">
          <label
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
            className={`flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-center cursor-pointer ${drag ? 'border-indigo-400 bg-indigo-50' : 'border-slate-300 hover:bg-slate-50'}`}
          >
            <Upload size={20} className="text-slate-400" />
            <span className="text-sm font-medium text-slate-700">파일을 끌어다 놓거나 눌러서 고르기</span>
            <span className="text-xs text-slate-400">공고문 · 제안요청서 · 과업지시서 · 문의 메일 — PDF · HWP · HWPX · DOCX, {MAX_FILES}개까지</span>
            <input type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
          </label>
          {files.length > 0 && (
            <ul className="space-y-1">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-sm">
                  <FileText size={14} className="text-slate-400 flex-shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-slate-800">{f.name}</span>
                  <span className="text-xs text-slate-400">{fmtSize(f.size)}</span>
                  <button onClick={() => setFiles(files.filter((_, j) => j !== i))} title="빼기" className="p-0.5 rounded text-slate-400 hover:bg-slate-100"><X size={14} /></button>
                </li>
              ))}
            </ul>
          )}
          <label className="block text-xs font-medium text-slate-500">글 붙여넣기 — 문의 메일 · 통화 메모 · 홈페이지 문의
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} maxLength={100000}
              placeholder="예: OO병원 기획팀 김OO 과장, 내년 의료데이터 활용 전략 컨설팅 견적 요청. 예산 1억 내외, 11월 중 제안 희망"
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </label>
          <p className="text-xs text-slate-400">AI 는 원문에 없는 값을 만들지 않습니다. 못 찾은 칸은 비워 두고 「확인 필요」로 표시합니다.</p>
        </div>
      )}

      {step === 'reading' && (
        <div className="py-16 text-center">
          <Loader2 size={28} className="mx-auto animate-spin text-indigo-500" />
          <p className="mt-3 text-sm font-medium text-slate-800">접수서를 채우고 있습니다 · {secs}초</p>
          <p className="mt-1 text-xs text-slate-500">보통 1~2분 걸립니다. 창을 닫지 마세요</p>
          <p className="mt-3 text-xs text-slate-400">{files.length ? `파일 ${files.length}개` : ''}{files.length && text.trim() ? ' · ' : ''}{text.trim() ? `글 ${text.trim().length.toLocaleString()}자` : ''}</p>
        </div>
      )}

      {step === 'confirm' && (
        <div className="space-y-4">
          {read.length > 0 && (
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <p className="font-medium text-slate-700 mb-1">읽은 파일 · 확인 필요 {missing}칸</p>
              {read.map((r) => (
                <p key={r.name} className="truncate">{r.status === '읽음' ? '✓' : '⚠'} {r.name} <span className="text-slate-400">· {r.status}{r.chars ? ` · ${r.chars.toLocaleString()}자` : ''}</span></p>
              ))}
            </div>
          )}
          <IntakeFields value={form} onChange={setForm} />
          <p className="text-xs text-slate-400">
            만들면: 검토 단계 프로젝트 · 드라이브 사업 폴더 「01.공고문·제안요청서」에 {files.length ? `원본 ${files.length}개와 ` : ''}접수서 Word 저장
            {files.length ? ' · 원본은 첨부에도 들어가 배점표 · 제안서 초안이 읽습니다' : ''}
          </p>
        </div>
      )}
    </SidePanel>
  );
};
